import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';

const Services = ({ services }) => {
  if (!services || services.length === 0) {
    return null;
  }

  return (
    <section id="services" className="py-24 md:py-32 bg-[#121212]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="text-[#d4af37] text-sm tracking-[0.3em] uppercase font-light mb-4">
            What We Offer
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#e5e5e5]">
            Our <span className="italic">Services</span>
          </h2>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative overflow-hidden bg-[#1a1a1a] border border-[#262626] hover:border-[#d4af37]/30 transition-all duration-500"
              data-testid={`service-card-${service.id}`}
            >
              {/* Image */}
              <div className="relative h-64 md:h-80 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-[#1a1a1a]/50 to-transparent" />
                
                {/* Price Badge */}
                <div className="absolute top-4 right-4 bg-[#d4af37] px-4 py-2">
                  <p className="text-[#0a0a0a] text-xs tracking-wider font-medium">
                    Starting at {service.starting_price}
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 md:p-8 space-y-4">
                <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#e5e5e5] group-hover:text-[#d4af37] transition-colors">
                  {service.title}
                </h3>
                
                <p className="text-[#a3a3a3] leading-relaxed line-clamp-2">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 pt-4">
                  {service.features.slice(0, 4).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-[#a3a3a3] text-sm">
                      <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className="pt-4">
                  <button
                    className="flex items-center gap-2 text-[#d4af37] text-sm tracking-wider uppercase group/btn"
                    onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
                    data-testid={`service-learn-more-${service.id}`}
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-2 transition-transform" />
                  </button>
                </div>
              </div>

              {/* Hover Border Effect */}
              <div className="absolute bottom-0 left-0 w-full h-1 bg-[#d4af37] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
